import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { ProductHighlightCardWidget } from "@brandclub/bc-widgets";
import { getAppConsistentParams } from "../../utils/appUtils.js";
import { getProductUrl } from "../../utils/productUtils.js";
import { decodeHTMLCharacters, formatMoney } from "../../utils/stringUtils.js";
import { useBrandAppConfig } from "../../api/cache/appConfigQueryOptions.js";
import { useProductClickTracking } from "../hooks/useProductClickTracking.js";
import { useConciergeContext } from "../ConciergeContextProvider.js";
import { fetchProductWithVariants } from "../api.js";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
const getFontWeight = (weight) => {
  const weightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  };
  return weightMap[weight] || 500;
};
const formatReviewCount = (count) => {
  if (count >= 1e3) {
    const k = count / 1e3;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return count.toLocaleString();
};
const StarIcon = ({ color = "#ED792F" }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "12",
    height: "12",
    viewBox: "0 0 16 16",
    fill: "none",
    style: { flexShrink: 0 },
    children: /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M15.4819 6.07562C15.4285 5.8977 15.2864 5.76804 15.1153 5.74117L10.3089 4.98558L8.1594 0.273902C8.0829 0.106169 7.92499 0 7.75211 0C7.5792 0 7.42132 0.106169 7.3448 0.273902L5.19518 4.98558L0.388901 5.74117C0.217838 5.76804 0.0756731 5.89771 0.0222526 6.07558C-0.0311937 6.2535 0.0133808 6.44878 0.137199 6.57932L3.61497 10.2469L2.7941 15.4256C2.76485 15.6099 2.83492 15.7962 2.97479 15.9062C3.05391 15.9684 3.14764 16 3.24182 16C3.31413 16 3.38668 15.9814 3.45312 15.9436L7.75208 13.4985L12.0509 15.9435C12.204 16.0306 12.3894 16.0161 12.5292 15.9061C12.6691 15.7962 12.7392 15.6099 12.71 15.4255L11.8888 10.2469L15.367 6.5793C15.4908 6.44879 15.5354 6.2535 15.4819 6.07562H15.4819Z",
        fill: color
      }
    )
  }
);
const ChevronIcon = ({ direction }) => /* @__PURE__ */ jsx("svg", { fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", style: { width: 14, height: 14, color: "#374151" }, children: direction === "left" ? /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2.5, d: "M15 19l-7-7 7-7" }) : /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2.5, d: "M9 5l7 7-7 7" }) });
const imageGalleryStyles = `
  .phc-gallery-scroll::-webkit-scrollbar { display: none; }
  .phc-gallery-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%) scale(0.92);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255,255,255,0.95);
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0,0,0,0.10);
    opacity: 0;
    transition: opacity 0.2s ease, transform 0.2s ease, background 0.15s ease;
    pointer-events: none;
    backdrop-filter: blur(4px);
  }
  .phc-gallery:hover .phc-gallery-arrow {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(-50%) scale(1);
  }
  .phc-gallery-arrow:hover { background: #f9fafb; box-shadow: 0 2px 12px rgba(0,0,0,0.14); }
`;
const ProductImageGallery = ({ images, title, borderRadius }) => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 10);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows);
    return () => el.removeEventListener("scroll", updateArrows);
  }, [updateArrows, images]);
  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth;
    el.scrollTo({ left: el.scrollLeft + (dir === "right" ? amount : -amount), behavior: "smooth" });
  };
  if (images.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: "phc-image", style: { aspectRatio: "1 / 1", borderRadius, backgroundColor: "#ffffff" } });
  }
  if (images.length === 1) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "phc-image",
        style: {
          aspectRatio: "1 / 1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius,
          backgroundColor: "#ffffff",
          overflow: "hidden"
        },
        children: /* @__PURE__ */ jsx("img", { src: images[0], alt: title, style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" } })
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "phc-image phc-gallery",
      style: { position: "relative", borderRadius, backgroundColor: "#ffffff", overflow: "hidden" },
      children: [
        /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: imageGalleryStyles } }),
        showLeft && /* @__PURE__ */ jsx(
          "button",
          {
            className: "phc-gallery-arrow",
            style: { left: 6 },
            onClick: () => scroll("left"),
            "aria-label": "Previous image",
            children: /* @__PURE__ */ jsx(ChevronIcon, { direction: "left" })
          }
        ),
        showRight && /* @__PURE__ */ jsx(
          "button",
          {
            className: "phc-gallery-arrow",
            style: { right: 6 },
            onClick: () => scroll("right"),
            "aria-label": "Next image",
            children: /* @__PURE__ */ jsx(ChevronIcon, { direction: "right" })
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            ref: scrollRef,
            className: "phc-gallery-scroll",
            style: {
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollbarWidth: "none"
            },
            children: images.map((url, idx) => /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  flexShrink: 0,
                  width: "100%",
                  aspectRatio: "1 / 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  scrollSnapAlign: "start"
                },
                children: /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: url,
                    alt: `${title} - ${idx + 1}`,
                    style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
                    loading: idx === 0 ? "eager" : "lazy"
                  }
                )
              },
              idx
            ))
          }
        )
      ]
    }
  );
};
const responsiveStyles = `
  .phc-root { container-type: inline-size; }
  .phc-layout { display: flex; flex-direction: column; border: none !important; padding: 0 !important; }
  .phc-image { width: 100%; }
  .phc-title-link { text-decoration: none; }
  .phc-title-link:hover { text-decoration: none; }
  .phc-cta { transition: filter 0.2s ease !important; align-self: stretch !important; margin-top: 0 !important; }
  .phc-cta:hover { filter: brightness(1.1); }
  .phc-variant-pill { transition: all 0.15s ease; }
  .phc-variant-pill:not(.phc-variant-selected):hover { background-color: rgba(0,0,0,0.05) !important; }
  .phc-variant-row { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; }
  .phc-variant-row::-webkit-scrollbar { display: none; }
  @container (min-width: 600px) {
    .phc-layout {
      flex-direction: row;
      align-items: stretch;
      border: 1px solid var(--phc-border-color) !important;
      padding: var(--phc-padding) !important;
    }
    .phc-image { width: 180px; flex-shrink: 0; align-self: flex-start; }
    .phc-content { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; }
    .phc-cta { align-self: flex-start !important; margin-top: 0 !important; }
    .phc-variant-row { flex-wrap: wrap; overflow-x: visible; }
  }
`;
const SKELETON_BG = "#F3F4F6";
const SkeletonCard = ({ product, reason }) => {
  const hasRating = product ? product.reviewsRating > 0 || product.reviewsAverageRating > 0 : true;
  const hasReason = !!reason;
  return /* @__PURE__ */ jsxs("div", { className: "phc-root", style: { margin: "12px 0" }, children: [
    /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: responsiveStyles } }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "phc-layout tw:animate-pulse",
        style: {
          "--phc-border-color": "#E5E7EB",
          "--phc-padding": "12px",
          gap: 16,
          borderRadius: 12,
          backgroundColor: "#fff",
          overflow: "hidden"
        },
        children: [
          /* @__PURE__ */ jsx("div", { className: "phc-image", style: { aspectRatio: "1 / 1", borderRadius: 8, backgroundColor: SKELETON_BG } }),
          /* @__PURE__ */ jsxs("div", { className: "phc-content", style: { display: "flex", flexDirection: "column", gap: 12, flex: 1 }, children: [
            /* @__PURE__ */ jsx("div", { style: { height: 66, width: "100%", backgroundColor: SKELETON_BG, borderRadius: 4 } }),
            hasRating && /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 4 }, children: [
              /* @__PURE__ */ jsx("div", { style: { width: 12, height: 12, borderRadius: 3, backgroundColor: SKELETON_BG, flexShrink: 0 } }),
              /* @__PURE__ */ jsx("div", { style: { height: 12, width: 70, backgroundColor: SKELETON_BG, borderRadius: 4 } })
            ] }),
            /* @__PURE__ */ jsx("div", { style: { marginTop: "auto" }, children: /* @__PURE__ */ jsx("div", { style: { height: 34, width: 160, backgroundColor: SKELETON_BG, borderRadius: 9999 } }) }),
            hasReason && /* @__PURE__ */ jsx("div", { style: { height: 16, width: "65%", backgroundColor: SKELETON_BG, borderRadius: 4 } })
          ] })
        ]
      }
    )
  ] });
};
const ProductHighlightCard = ({ stacklineSku, reason }) => {
  const { trackProductClick } = useProductClickTracking();
  const { data: brandAppConfig } = useBrandAppConfig();
  const { apiBaseUrl } = useConciergeContext();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVariantLoading, setIsVariantLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await fetchProductWithVariants({
          stacklineSku,
          headersToInject: headers,
          apiBaseUrl
        });
        if (!cancelled && result) setProduct(result);
      } catch {
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [stacklineSku, apiBaseUrl]);
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const targetUrl = product ? getProductUrl({
    title: product.title,
    stacklineSku: product.stacklineSku,
    urlSearchParams,
    dtcProductPageUrl: product.dtcProductPageUrl,
    appConfigId: brandAppConfig == null ? void 0 : brandAppConfig.appConfigId
  }) : "#";
  const handleClick = useCallback(() => {
    if (!product) return;
    trackProductClick({
      stacklineSku: product.stacklineSku,
      productTitle: product.title,
      clickSource: "highlight",
      retailerId: product.retailerId,
      retailPrice: product.retailPrice,
      hasReward: product.hasRewards,
      rewardAmount: product.totalRewardAmount
    });
  }, [trackProductClick, product]);
  useCallback(
    (variantSku) => {
      if (!(product == null ? void 0 : product.variants)) return;
      const variant = product.variants.find((v) => v.stacklineSku === variantSku);
      if (!variant) return;
      setProduct((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          stacklineSku: variant.stacklineSku,
          retailerSku: variant.retailerSku ?? prev.retailerSku,
          title: variant.title,
          variationNameValues: variant.variationNameValues ?? prev.variationNameValues,
          ...variant.imageUrls && { imageUrls: variant.imageUrls },
          ...variant.retailPrice != null && { retailPrice: variant.retailPrice },
          ...variant.subCategoryId != null && { subCategoryId: variant.subCategoryId },
          ...variant.dtcProductPageUrl && { dtcProductPageUrl: variant.dtcProductPageUrl },
          ...variant.retailerId != null && { retailerId: variant.retailerId }
        };
      });
    },
    [product == null ? void 0 : product.variants]
  );
  if (isLoading) return /* @__PURE__ */ jsx(SkeletonCard, {});
  if (isVariantLoading) return /* @__PURE__ */ jsx(SkeletonCard, { product, reason });
  if (!product) return null;
  const reviewRating = product.reviewsRating > 0 ? Math.round(product.reviewsRating * 10) / 10 : product.reviewsAverageRating > 0 ? Math.round(product.reviewsAverageRating * 10) / 10 : 0;
  const reviewCount = product.reviewsCount || 0;
  return /* @__PURE__ */ jsx(ProductHighlightCardWidget, { widgetId: "product-highlight-card", displayName: "Product Highlight Card", children: (state, resolveColor) => /* @__PURE__ */ jsxs("div", { className: "phc-root", style: { margin: "4px 0" }, children: [
    /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: responsiveStyles } }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "phc-layout",
        style: {
          "--phc-border-color": resolveColor(state.containerBorderColor),
          "--phc-padding": "8px",
          gap: 8,
          borderRadius: state.containerBorderRadius,
          backgroundColor: resolveColor(state.containerBgColor),
          overflow: "hidden"
        },
        children: [
          /* @__PURE__ */ jsx(
            ProductImageGallery,
            {
              images: product.imageUrls || [],
              title: product.title,
              borderRadius: state.imageBorderRadius
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "phc-content", style: { display: "flex", flexDirection: "column", gap: 14, flex: 1, justifyContent: "center" }, children: [
            /* @__PURE__ */ jsx(
              "a",
              {
                className: "phc-title-link",
                href: targetUrl,
                target: "_blank",
                rel: "noreferrer",
                onClick: handleClick,
                style: {
                  display: "-webkit-box",
                  fontSize: state.titleFontSize,
                  fontWeight: getFontWeight(state.titleFontWeight),
                  lineHeight: "1.4",
                  color: resolveColor(state.titleColor),
                  cursor: "pointer",
                  overflow: "hidden",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical"
                },
                children: decodeHTMLCharacters(product.title)
              }
            ),
            reviewRating > 0 && /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 4 }, children: [
              /* @__PURE__ */ jsx(StarIcon, { color: resolveColor(state.ratingStarColor) }),
              /* @__PURE__ */ jsxs(
                "span",
                {
                  style: {
                    fontSize: state.ratingFontSize,
                    fontWeight: 500,
                    color: resolveColor(state.ratingTextColor)
                  },
                  children: [
                    reviewRating,
                    " (",
                    formatReviewCount(reviewCount),
                    ")"
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs(
              "a",
              {
                className: "phc-cta",
                href: targetUrl,
                target: "_blank",
                rel: "noreferrer",
                onClick: handleClick,
                style: {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  alignSelf: "flex-start",
                  gap: 12,
                  paddingLeft: 24,
                  paddingRight: 24,
                  height: state.ctaButtonHeight,
                  backgroundColor: resolveColor(state.ctaButtonBgColor),
                  color: resolveColor(state.ctaButtonTextColor),
                  borderRadius: state.ctaButtonBorderRadius,
                  fontSize: state.ctaButtonFontSize,
                  fontWeight: getFontWeight(state.ctaButtonFontWeight),
                  letterSpacing: 0.3,
                  textDecoration: "none",
                  cursor: "pointer"
                },
                children: [
                  /* @__PURE__ */ jsx("span", { children: "View product" }),
                  /* @__PURE__ */ jsx(
                    "span",
                    {
                      style: {
                        width: 1,
                        height: 14,
                        backgroundColor: resolveColor(state.ctaButtonTextColor),
                        opacity: 0.35
                      }
                    }
                  ),
                  /* @__PURE__ */ jsx("span", { style: { fontWeight: getFontWeight("bold") }, children: formatMoney(product.retailPrice) })
                ]
              }
            ),
            reason && /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  fontSize: state.reasonFontSize,
                  fontStyle: "italic",
                  lineHeight: "1.4",
                  color: resolveColor(state.reasonTextColor),
                  opacity: 0.7
                },
                children: reason
              }
            )
          ] })
        ]
      }
    )
  ] }) });
};
export {
  ProductHighlightCard
};
//# sourceMappingURL=ProductHighlightCard.js.map
